import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  BugReport as BugIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Dashboard = () => {
  const [sheets, setSheets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editSheet, setEditSheet] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSheets();
  }, []);

  const fetchSheets = async () => {
    try {
      const response = await api.get('/sheets');
      setSheets(response.data);
    } catch (error) {
      setError('Failed to fetch sheets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editSheet) {
        await api.put(`/sheets/${editSheet._id}`, formData);
      } else {
        await api.post('/sheets', formData);
      }
      setOpen(false);
      setEditSheet(null);
      setFormData({ name: '', description: '' });
      fetchSheets();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save sheet');
    }
  };

  const handleDelete = async (sheetId) => {
    if (window.confirm('Are you sure? This will delete all bugs in this sheet.')) {
      try {
        await api.delete(`/sheets/${sheetId}`);
        fetchSheets();
      } catch (error) {
        setError('Failed to delete sheet');
      }
    }
  };

  const handleOpenDialog = (sheet = null) => {
    setEditSheet(sheet);
    setFormData({
      name: sheet?.name || '',
      description: sheet?.description || '',
    });
    setOpen(true);
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Bug Tracker Sheets
        </Typography>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Sheet
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {sheets?.map((sheet) => (
          <Grid item xs={12} sm={6} md={4} key={sheet._id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {sheet.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {sheet.description || 'No description'}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <BugIcon color="primary" />
                  <Typography variant="body2">
                    {sheet.bugCount} bugs
                  </Typography>
                </Box>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Created by: {sheet.createdBy?.name}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  onClick={() => navigate(`/sheet/${sheet._id}`)}
                >
                  View Bugs
                </Button>
                {user?.role === 'admin' && (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(sheet)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(sheet._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {sheets.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No sheets available
          </Typography>
          {user?.role === 'admin' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2 }}
            >
              Create Your First Sheet
            </Button>
          )}
        </Paper>
      )}

      {/* Create/Edit Sheet Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editSheet ? 'Edit Sheet' : 'Create New Sheet'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Sheet Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editSheet ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;